'use client'

import { useState, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ArrowRight, Sparkles, ChevronDown } from 'lucide-react'
import ConsultationModal from './modals/ConsultationModal'
import MeetingModal from './modals/MeetingModal'
import SmartImage from './ui/SmartImage'
import { siteImages } from '@/data/siteImages'
import { useCompanyInfo } from '@/contexts/CompanyInfoContext'

function AnimatedCounter({ value, suffix }: { value: number; suffix: string }) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    let start = 0
    const duration = 2000
    const step = value / (duration / 16)
    const timer = setInterval(() => {
      start += step
      if (start >= value) { setCount(value); clearInterval(timer) }
      else setCount(Math.floor(start))
    }, 16)
    return () => clearInterval(timer)
  }, [value])
  return <span>{count}{suffix}</span>
}

export default function Hero() {
  const [showConsultationModal, setShowConsultationModal] = useState(false)
  const [showMeetingModal, setShowMeetingModal] = useState(false)
  const { companyInfo } = useCompanyInfo()
  const { scrollY } = useScroll()
  const y1 = useTransform(scrollY, [0, 500], [0, -50])
  const y2 = useTransform(scrollY, [0, 500], [0, -100])
  const opacity = useTransform(scrollY, [0, 400], [1, 0])

  // Stats: real data where we have it, honest capability labels otherwise.
  // `value` null = render the text label instead of an animated number.
  const establishedYear = companyInfo?.businessInfo?.establishedYear
  const currentYear = new Date().getFullYear()
  const years = establishedYear && establishedYear <= currentYear ? currentYear - establishedYear : null
  const employeeMax = (() => {
    const v = companyInfo?.additionalInfo?.employeeCount
    const nums = v ? (v.match(/\d+/g) || []).map(Number) : []
    return nums.length ? Math.max(...nums) : null
  })()

  const stats: { value: number | null; suffix: string; text?: string; label: string }[] = [
    years && years > 0
      ? { value: years, suffix: '+', label: 'Years in Business' }
      : { value: null, suffix: '', text: 'Expert', label: 'Engineering Team' },
    employeeMax
      ? { value: employeeMax, suffix: '+', label: 'Team Members' }
      : { value: null, suffix: '', text: 'Full-Stack', label: 'Capabilities' },
    { value: null, suffix: '', text: 'End-to-End', label: 'Delivery' },
    { value: null, suffix: '', text: '24/7', label: 'Support' },
  ]

  return (
    <section id="home" className="relative min-h-screen overflow-hidden bg-gradient-to-br from-sky-50 via-white to-blue-50">
      {/* Animated Background Orbs */}
      <motion.div style={{ y: y1 }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-[10%] w-72 h-72 bg-sky-200/40 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-40 right-[15%] w-96 h-96 bg-blue-200/30 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-32 left-[30%] w-64 h-64 bg-sky-100/50 rounded-full blur-3xl animate-float-fast" />
        <div className="absolute top-1/2 right-[5%] w-48 h-48 bg-blue-300/20 rounded-full blur-2xl animate-pulse-slow" />
      </motion.div>

      {/* Geometric Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0ea5e9 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      </div>

      {/* Morphing blob background - hidden on mobile for performance */}
      <div className="hidden md:block absolute top-1/3 right-[10%] w-[500px] h-[500px] bg-gradient-to-br from-sky-200/20 to-blue-300/20 blob-morph" />

      {/* Main Content */}
      <motion.div style={{ opacity }} className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-32">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center min-h-[60vh] lg:min-h-[70vh]">
            
            {/* Left: Text Content */}
            <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span className="section-tag">
                  <Sparkles size={14} className="inline mr-1.5 -mt-0.5 animate-pulse" />
                  Next-Gen IT Solutions
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="text-3xl xs:text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight"
              >
                <span className="text-slate-900">We Build </span>
                <motion.span 
                  className="gradient-text-bold inline-block"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.7, type: 'spring', stiffness: 200 }}
                >Digital</motion.span>
                <br className="hidden sm:block" />
                <span className="text-slate-900"> Experiences That </span>
                <motion.span 
                  className="gradient-text-bold inline-block"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.9, type: 'spring', stiffness: 200 }}
                >Matter</motion.span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-lg mx-auto lg:mx-0 leading-relaxed"
              >
                Transform your business with cutting-edge software solutions, cloud services, and AI-powered innovations that drive real results.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="flex flex-col xs:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start"
              >
                <motion.button
                  onClick={() => setShowConsultationModal(true)}
                  className="btn-primary flex items-center justify-center gap-2 group"
                  whileHover={{ scale: 1.03, boxShadow: '0 20px 40px rgba(14,165,233,0.3)' }}
                  whileTap={{ scale: 0.97 }}
                >
                  Get Free Consultation
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button
                  onClick={() => setShowMeetingModal(true)}
                  className="btn-secondary"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Schedule a Meeting
                </motion.button>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 pt-6 sm:pt-8 border-t border-sky-100"
              >
                {stats.map((stat, i) => (
                  <motion.div 
                    key={i} 
                    className="text-center lg:text-left"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1 + i * 0.1 }}
                  >
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900">
                      {stat.value !== null ? <AnimatedCounter value={stat.value} suffix={stat.suffix} /> : stat.text}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{stat.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Right: Visual Element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: 50 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ y: y2 }}
              className="hidden lg:block relative"
            >
              <div className="relative">
                {/* Real photo backdrop — framed, with gradient overlay */}
                <motion.div
                  className="absolute -top-10 -right-8 w-[420px] h-[300px] rounded-3xl overflow-hidden shadow-2xl shadow-sky-200/50 ring-1 ring-white/40"
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <SmartImage
                    src={siteImages.hero.main}
                    alt="Our team collaborating on digital products"
                    fill
                    priority
                    sizes="420px"
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-sky-600/50 via-blue-500/20 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                </motion.div>

                {/* Main glass card with glow */}
                <motion.div 
                  className="glass rounded-3xl p-8 relative z-10 glow-sky mt-24"
                  whileHover={{ rotateY: 2, rotateX: -2 }}
                  transition={{ type: 'spring', stiffness: 100 }}
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                      <span className="text-xs text-slate-400 ml-2 font-mono">project.tsx</span>
                    </div>
                    <motion.div 
                      className="space-y-2 font-mono text-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.2, staggerChildren: 0.1 }}
                    >
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3 }} className="text-sky-600">{'import'} <span className="text-slate-700">{'{ Innovation }'}</span> <span className="text-sky-600">from</span> <span className="text-green-600">{'"@company/core"'}</span></motion.div>
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.5 }} className="text-slate-400 mt-3">{'// Building the future'}</motion.div>
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.7 }} className="text-purple-600">{'export const'} <span className="text-slate-700">{'solution'}</span> = {'{'}</motion.div>
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.9 }} className="text-slate-600 pl-4">{'quality: '}<span className="text-sky-500">{'"exceptional"'}</span>,</motion.div>
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 2.1 }} className="text-slate-600 pl-4">{'delivery: '}<span className="text-sky-500">{'"on-time"'}</span>,</motion.div>
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 2.3 }} className="text-slate-600 pl-4">{'innovation: '}<span className="text-orange-500">{'true'}</span></motion.div>
                      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 2.5 }} className="text-purple-600">{'}'}</motion.div>
                    </motion.div>
                  </div>
                </motion.div>
                {/* Decorative floating cards */}
                <motion.div
                  animate={{ y: [-8, 8, -8], rotate: [0, 1, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -top-6 -right-6 bg-white rounded-xl shadow-lg shadow-sky-100/50 p-4 border border-sky-100"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"><span className="text-green-600 text-xs font-bold">✓</span></div>
                    <div><p className="text-xs font-semibold text-slate-700">Deployed</p><p className="text-[10px] text-slate-400">Just now</p></div>
                  </div>
                </motion.div>
                <motion.div
                  animate={{ y: [8, -8, 8], rotate: [0, -1, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -bottom-4 -left-6 bg-white rounded-xl shadow-lg shadow-sky-100/50 p-4 border border-sky-100"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center"><span className="text-sky-600 text-xs font-bold">⚡</span></div>
                    <div><p className="text-xs font-semibold text-slate-700">99.9% Uptime</p><p className="text-[10px] text-slate-400">Last 30 days</p></div>
                  </div>
                </motion.div>
                {/* New: Floating tech stack badge */}
                <motion.div
                  animate={{ x: [-5, 5, -5], y: [3, -3, 3] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute top-1/2 -right-12 bg-gradient-to-r from-sky-500 to-blue-600 rounded-lg px-3 py-1.5 shadow-lg shadow-sky-500/30"
                >
                  <p className="text-[10px] text-white font-medium">React • Next.js • Node</p>
                </motion.div>
              </div>
            </motion.div>

            {/* Mobile: Simplified visual card (shown on small screens) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="lg:hidden mt-4"
            >
              <div className="glass rounded-2xl p-5 max-w-sm mx-auto">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-sky-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{'</>'}</span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">Full-Stack Development</p>
                      <p className="text-[10px] text-slate-400">React • Next.js • Node.js</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-[10px] text-green-600 font-medium">Active</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center gap-1 text-slate-400"
          >
            <span className="text-xs font-medium">Scroll to explore</span>
            <ChevronDown size={18} />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Modals */}
      {showConsultationModal && <ConsultationModal isOpen={showConsultationModal} onClose={() => setShowConsultationModal(false)} />}
      {showMeetingModal && <MeetingModal isOpen={showMeetingModal} onClose={() => setShowMeetingModal(false)} />}
    </section>
  )
}
